import React, { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { X, Mail, QrCode, MessageCircle, Smartphone, Copy, Check } from "lucide-react";
import { staffApi } from "@/lib/staffApi";

/**
 * SendMedicalFormModal
 *
 * Angela picks:
 *   1. Which Medical Form template (default = mapped to booked service)
 *   2. Email, WhatsApp, both together, QR, or open on this device
 *
 * A single POST /appointments/{id}/send-medical-form kicks off a dispatch and
 * returns a URL. We render the URL directly (QR image, copy button, or window.open).
 */
export function SendMedicalFormModal({ appointment, onClose, onSent, formKind = "medical", selectedTemplateId = null }) {
    const label = formKind === "consultation" ? "Consultation" : formKind === "aftercare" ? "Aftercare" : "Medical";
    const [assignedIds, setAssignedIds] = useState([]);
    const [templates, setTemplates] = useState(null);
    const [templateId, setTemplateId] = useState(null);
    const [channel, setChannel] = useState("email");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);           // { link, template, channel }
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState("");
    const sendAttempt = useRef(null);
    const sending = useRef(false);

    // Use service assignments, never service-name guesses. Opening this picker sends nothing.
    useEffect(() => {
        let active=true;
        const kinds=formKind === "consultation" ? ['consultation','general_consultation','treatment_consultation'] : formKind === 'aftercare' ? ['aftercare','aftercare_acknowledgement'] : ['medical','medical_history','treatment_consent'];
        Promise.all([staffApi.listForms(), appointment.serviceId ? staffApi.listServiceForms(appointment.serviceId) : Promise.resolve({assignments:[]})])
            .then(([r,mapping]) => {
                if(!active)return;
                const ids=(mapping.assignments || []).map(a=>a.form_template_id);
                const forms=(r.forms || []).filter(f=>kinds.includes(f.form_kind || f.category) && f.status === 'published' && (!selectedTemplateId || f.id === selectedTemplateId));
                const assigned=forms.filter(f=>ids.includes(f.id));
                setAssignedIds(ids);setTemplates([...assigned,...forms.filter(f=>!ids.includes(f.id))]);
                setTemplateId(selectedTemplateId || (assigned.length===1 ? assigned[0].id : null));
            }).catch(e=>{if(active){setTemplates([]);setError(e.message || 'Could not load forms. Close and try again.');}});
        return ()=>{active=false;};
    }, [appointment.serviceId, formKind, selectedTemplateId]);

    const chosen = useMemo(
        () => (templates || []).find(t => t.id === templateId),
        [templates, templateId],
    );

    const cust = appointment.customer || {};
    const hasEmail = !!(cust.email || "").trim();
    const hasPhone = !!(cust.phone || "").trim();
    const isMessage = ["email", "whatsapp", "both"].includes(channel);
    const unavailable = (channel === "email" && !hasEmail) || (channel === "whatsapp" && !hasPhone) || (channel === "both" && (!hasEmail || !hasPhone));

    const doSend = async () => {
        if (sending.current || unavailable || !chosen) return;
        sending.current = true;
        setBusy(true); setCopied(false); setError("");
        try {
            const payload = { template_id: templateId, channel, form_kind: formKind };
            if (channel === "both") {
                const key = `${appointment.id}:${templateId}:${formKind}`;
                if (sendAttempt.current?.key !== key) {
                    const bytes = new Uint8Array(16);
                    window.crypto.getRandomValues(bytes);
                    bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
                    const hex = Array.from(bytes, value => value.toString(16).padStart(2, "0")).join("");
                    sendAttempt.current = { key, id: `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}` };
                }
                payload.request_id = sendAttempt.current.id;
            }
            const r = await staffApi.sendMedicalForm(appointment.id, payload);
            setResult(r);
            if (isMessage) {
                toast.success(channel === "both" ? "Email and WhatsApp messages queued." : `${label} form message queued.`);
            } else if (channel === "device") {
                // Open the secure link right here on Angela's device.
                window.open(r.link, "_blank", "noopener");
                onSent?.(r);
            }
        } catch (e) {
            setError(e.message || `Could not send ${label.toLowerCase()} form.`);
            toast.error(e.message || `Could not send ${label.toLowerCase()} form.`);
        } finally { sending.current = false; setBusy(false); }
    };

    const copyLink = async () => {
        if (!result?.link) return;
        try {
            await navigator.clipboard.writeText(result.link);
            setCopied(true);
            toast.success("Secure link copied to clipboard.");
            setTimeout(() => setCopied(false), 2500);
        } catch { toast.error("Copy failed — please long-press the link."); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/80 backdrop-blur-sm p-3" data-testid="send-medical-modal">
            <div className="w-full max-w-lg rounded-2xl bg-navy border border-gold/40 p-5 md:p-6 relative max-h-[92vh] overflow-y-auto">
                <button type="button" onClick={onClose} aria-label="Close" data-testid="send-medical-close"
                        className="absolute top-3 right-3 text-white/60 hover:text-white">
                    <X className="h-5 w-5" />
                </button>

                <div className="text-[10px] uppercase tracking-[0.28em] text-gold/85">{label} form</div>
                <h2 className="font-serif text-2xl text-white mt-1">Send to {cust.firstName || "customer"}</h2>

                {/* Template picker */}
                <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1.5">Form template</div>
                    {templates == null ? (
                        <div className="text-xs text-white/50">Loading templates…</div>
                    ) : templates.length === 0 ? (
                        <div className="text-xs text-amber-200">No published {label.toLowerCase()} forms found. Create and publish one in Forms.</div>
                    ) : (
                        <select value={templateId || ""} onChange={(e) => setTemplateId(e.target.value)} disabled={busy || !!result}
                                data-testid="send-medical-template"
                                className="w-full bg-transparent border border-white/15 rounded-lg text-white p-2 text-sm focus:border-gold outline-none">
                            <option value="" disabled>Choose a form</option>
                            {templates.map(t => (
                                <option key={t.id} value={t.id} className="bg-navy">
                                    {t.name}{assignedIds.includes(t.id) ? " — assigned to this service" : ""}
                                </option>
                            ))}
                        </select>
                    )}
                    {chosen && !assignedIds.includes(chosen.id) && (
                        <div className="mt-1 text-[11px] text-amber-200/80">
                            This form is not assigned to this service. Check it is the form you want to send.
                        </div>
                    )}
                </div>

                {/* Channel picker */}
                <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1.5">How to send</div>
                    <fieldset disabled={busy || !!result} className="grid grid-cols-2 gap-2">
                        <ChannelBtn icon={Mail} label="Email" selected={channel === "email"} onClick={() => setChannel("email")}
                                    disabled={!hasEmail} testId="send-medical-ch-email"
                                    hint={!hasEmail && "No email on file"} />
                        <ChannelBtn icon={MessageCircle} label="WhatsApp" selected={channel === "whatsapp"} onClick={() => setChannel("whatsapp")} disabled={!hasPhone} hint={!hasPhone && "No phone number on file"} testId="send-medical-ch-whatsapp" />
                        <ChannelBtn icon={Mail} label="Email + WhatsApp" selected={channel === "both"} onClick={() => setChannel("both")}
                                    disabled={!hasEmail || !hasPhone} testId="send-medical-ch-both" wide
                                    hint={!hasEmail || !hasPhone ? "An email address and phone number are needed" : "Send the same form through both channels"} />
                        <ChannelBtn icon={QrCode} label="Show QR code" selected={channel === "qr"} onClick={() => setChannel("qr")} testId="send-medical-ch-qr" />
                        <ChannelBtn icon={Smartphone} label="Open on this device" selected={channel === "device"} onClick={() => setChannel("device")} testId="send-medical-ch-device" />
                    </fieldset>
                </div>

                {error && <div role="alert" className="mt-3 rounded-lg bg-red-50 text-red-900 p-3 text-sm">{error}</div>}
                {/* Action */}
                {!result && (
                    <div className="mt-5 flex justify-end gap-2">
                        <button type="button" onClick={onClose} className="rounded-full h-10 px-4 text-white/70 text-sm hover:text-white">Cancel</button>
                        <button type="button" disabled={busy || !chosen || unavailable}
                                onClick={doSend} data-testid="send-medical-go"
                                className="rounded-full h-10 px-5 bg-gold text-navy font-semibold text-sm disabled:opacity-40">
                            {busy ? "Sending…" : channel === "both" ? "Send both" : channel === "email" ? "Send email" : channel === "whatsapp" ? "Send WhatsApp" : channel === "qr" ? "Generate QR" : "Open form"}
                        </button>
                    </div>
                )}

                {/* Result surfaces */}
                {result && channel === "qr" && (
                    <div className="mt-5 flex flex-col items-center gap-3" data-testid="send-medical-qr-panel">
                        <div className="bg-white p-4 rounded-xl">
                            <QRCodeSVG value={result.link} size={220} level="M" />
                        </div>
                        <div className="text-xs text-white/70 text-center">
                            Ask {cust.firstName || "the customer"} to scan with their phone camera.<br />
                            {formKind === "aftercare" ? "The link stays available for 30 days." : "The same form only needs to be completed once."}
                        </div>
                    </div>
                )}
                {result && channel === "link" && (
                    <div className="mt-5 rounded-xl border border-gold/40 bg-gold/5 p-3" data-testid="send-medical-link-panel">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1.5">Secure link</div>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs text-white break-all">{result.link}</code>
                            <button type="button" onClick={copyLink} data-testid="send-medical-copy-link"
                                    className="rounded-full h-8 px-3 border border-white/25 text-xs text-white/80 hover:text-gold flex items-center gap-1">
                                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}
                            </button>
                        </div>
                    </div>
                )}
                {result && isMessage && (
                    <div className="mt-5 rounded-xl border border-emerald-400/40 bg-emerald-500/10 p-3 text-emerald-100 text-sm" data-testid="send-medical-email-sent">
                        {channel === "both" ? "Email and WhatsApp queued with the same form link. The form only needs to be completed once." : `${channel === "whatsapp" ? "WhatsApp" : "Email"} queued.`} Check Communications for delivery status.
                    </div>
                )}

                {result && (
                    <div className="mt-4 flex justify-end">
                        <button type="button" onClick={() => { setResult(null); onSent?.(result); onClose(); }}
                                className="text-white/70 text-sm hover:text-white" data-testid="send-medical-done">
                            Done
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function ChannelBtn({ icon: Icon, label, selected, onClick, disabled, hint, testId, wide }) {
    return (
        <button type="button" onClick={onClick} disabled={disabled} data-testid={testId} aria-pressed={selected}
                className={`flex items-center gap-2 rounded-xl border p-2.5 text-sm transition-colors text-left ${wide ? "col-span-2" : ""}
                    ${selected ? "border-gold bg-gold/10 text-white" : "border-white/15 text-white/80 hover:border-white/40"}
                    ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}>
            <Icon className="h-4 w-4 shrink-0" />
            <div>
                <div>{label}</div>
                {hint && <div className="text-[10px] text-white/50">{hint}</div>}
            </div>
        </button>
    );
}
